import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAutoScrollOptions {
  /** Threshold in pixels from bottom to consider "near bottom" */
  threshold?: number
}

interface UseAutoScrollReturn {
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement>
  /** Ref to attach to the scroll anchor element at the bottom */
  anchorRef: React.RefObject<HTMLDivElement>
  /** Whether to show the scroll-to-bottom button */
  showScrollButton: boolean
  /** Whether user has manually scrolled up */
  userHasScrolledUp: boolean
  /** Scroll to bottom - instant during streaming, smooth otherwise */
  scrollToBottom: (instant?: boolean) => void
  /** Handle scroll events - attach to container's onScroll */
  handleScroll: () => void
  /** Reset scroll state (call when sending new message) */
  resetScrollState: () => void
}

/**
 * Custom hook for managing auto-scroll behavior in chat interfaces.
 * Handles scroll tracking, scroll-to-bottom functionality, and user scroll detection.
 */
export function useAutoScroll(options: UseAutoScrollOptions = {}): UseAutoScrollReturn {
  const { threshold = 100 } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false)

  // Scroll to bottom function - uses instant scroll during streaming for better UX
  const scrollToBottom = useCallback((instant = false) => {
    const container = containerRef.current
    if (container) {
      if (instant) {
        // Instant scroll - better for streaming content
        container.scrollTop = container.scrollHeight
      } else {
        // Smooth scroll - better for user-initiated actions
        anchorRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
    setUserHasScrolledUp(false)
  }, [])

  // Handle scroll events to show/hide scroll button
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const isNearBottom = distanceFromBottom < threshold

    setShowScrollButton(!isNearBottom)

    // Track if user manually scrolled up
    if (!isNearBottom) {
      setUserHasScrolledUp(true)
    }
  }, [threshold])

  // Reset scroll state
  const resetScrollState = useCallback(() => {
    setUserHasScrolledUp(false)
  }, [])

  return {
    containerRef,
    anchorRef,
    showScrollButton,
    userHasScrolledUp,
    scrollToBottom,
    handleScroll,
    resetScrollState,
  }
}

interface UseAutoScrollEffectOptions {
  /** Messages array to track changes */
  messages: { content?: string; isStreaming?: boolean; role: string }[]
  /** Whether user has scrolled up */
  userHasScrolledUp: boolean
  /** Scroll to bottom function */
  scrollToBottom: (instant?: boolean) => void
}

/**
 * Effect hook that handles auto-scrolling when messages change.
 * Uses instant scroll during streaming to keep up with rapid updates.
 */
export function useAutoScrollEffect({
  messages,
  userHasScrolledUp,
  scrollToBottom,
}: UseAutoScrollEffectOptions): void {
  // Get the last message to track streaming content
  const lastMessage = messages[messages.length - 1]
  const isLastMessageStreaming = lastMessage?.role === 'assistant' && lastMessage?.isStreaming
  const lastMessageContent = lastMessage?.content

  // Auto-scroll when messages change or when streaming content updates
  useEffect(() => {
    if (!userHasScrolledUp && messages.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        // Use instant scroll during streaming to keep up with rapid updates
        scrollToBottom(isLastMessageStreaming)
      })
    }
  }, [messages.length, lastMessageContent, isLastMessageStreaming, userHasScrolledUp, scrollToBottom])
}

